import React, { useCallback, useMemo, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DoneIcon from "@mui/icons-material/Done";
import EditIconOutlined from "@mui/icons-material/EditOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Button, Chip, Collapse, IconButton, Popover, Stack, Typography } from "@mui/material";
import { PsychologyReverse } from "assets/material-custom-icons";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { NumberInput } from "components/forms/Number-input/NumberInput";
import { useFormik } from "formik";
import { useActionHook, useModals } from "hooks";
import { usePickUserPermissions, usePortfolioConstructionActions } from "store";
import { IPortfolioConstructionInvestmentGoal, UserPermissionType } from "store/types";

import { NoteHintPopover } from "./NoteHintPopover";

interface FormValues {
  aum: string | null;
  target_return: string | null;
  target_volatility: string | null;
  max_drawdown: string | null;
  liquid_month_0_3: string | null;
  liquid_month_3_12: string | null;
  liquid_year_1_3: string | null;
  liquid_year_3_10: string | null;
  liquid_year_10_plus: string | null;
  is_ai: boolean;
  ai_note: string | null;
}

// const validationSchema = Yup.object().shape({
//   aum: Yup.string()
//     .transform((value) => (isNaN(value) ? undefined : value))
//     .required("Required"),
//   liquidity: Yup.string().required("Required"),
//   target_return: Yup.number()
//     .transform((value) => (isNaN(value) ? undefined : value))
//     .min(0, "Must be positive")
//     .max(25, "Target return cannot exceed 25%")
//     .required("Required"),
//   target_volatility: Yup.string().required("Required"),
//   max_drawdown: Yup.string().required("Required"),
//   liquid_month_0_3: Yup.number()
//     .transform((value) => (isNaN(value) ? undefined : value))
//     .min(0, "Must be positive")
//     .max(100, "Must be less than 100")
//     .required("Required"),
//   liquid_month_3_12: Yup.number()
//     .transform((value) => (isNaN(value) ? undefined : value))
//     .min(0, "Must be positive")
//     .max(100, "Must be less than 100")
//     .required("Required"),
//   liquid_year_1_3: Yup.number()
//     .transform((value) => (isNaN(value) ? undefined : value))
//     .min(0, "Must be positive")
//     .max(100, "Must be less than 100")
//     .required("Required"),
//   liquid_year_3_10: Yup.number()
//     .transform((value) => (isNaN(value) ? undefined : value))
//     .min(0, "Must be positive")
//     .max(100, "Must be less than 100")
//     .required("Required"),
//   liquid_year_10_plus: Yup.number()
//     .transform((value) => (isNaN(value) ? undefined : value))
//     .min(0, "Must be positive")
//     .max(100, "Must be less than 100")
//     .required("Required")
// });

// const validationSchema = Yup.object().shape({
//   target_return: Yup.string()
//     .transform((value) => (isNaN(value) ? undefined : value))
//     .when("is_ai", {
//       is: true,
//       then: (schema) =>
//         schema.required("Required").min(0, "Must be positive").max(25, "Target return cannot exceed 25%"),
//       otherwise: (schema) => schema.optional()
//     })
// });

interface Props {
  investorId: number;
  setRefresh: () => void;
  data: IPortfolioConstructionInvestmentGoal | undefined;
  setData: React.Dispatch<React.SetStateAction<IPortfolioConstructionInvestmentGoal | undefined>>;
  onRemoveAIConstruction: () => void;
  loading: boolean;
}

export const InvestmentGoalsForm: React.FC<Props> = ({
  investorId,
  setRefresh,
  data,
  setData,
  onRemoveAIConstruction,
  loading
}) => {
  const featurePermission = usePickUserPermissions("portfolio_construction");
  const isViewerPermission = featurePermission === UserPermissionType.VIEWER;

  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popupAnchor, setPopupAnchor] = useState<HTMLButtonElement | null>(null);
  const { infoModal } = useModals();

  const { createInvestmentGoalsAction } = usePortfolioConstructionActions();
  const { action: createGoals, loading: createGoalsLoading } = useActionHook(createInvestmentGoalsAction, {
    onSuccess: (res) => {
      if (res.aum !== data?.aum && !res.is_ai) {
        //if user saved manually and aum changed, refresh
        setRefresh();
      }

      setData(res);
      setIsOpen(false);
      res.ai_note &&
        infoModal({
          title: "AI Construction",
          msg: res.ai_note,
          isHTML: true,
          action: setRefresh
        });
    },
    onReject: (error) => {
      if (error.errors) {
        Object.keys(error.errors).forEach((key) => {
          setFieldError(key, error.errors[key][0]);
        });
      }
    }
  });

  const getFormValues = useCallback(() => {
    if (data && !!Object.keys(data).length) {
      return {
        ...data,
        aum: data.aum?.toString() || "",
        max_drawdown: data.max_drawdown?.toString() || "",
        target_return: data.target_return?.toString() || "",
        target_volatility: data.target_volatility?.toString() || "",
        liquid_month_0_3: data.liquid_month_0_3?.toString() || "",
        liquid_month_3_12: data.liquid_month_3_12?.toString() || "",
        liquid_year_1_3: data.liquid_year_1_3?.toString() || "",
        liquid_year_3_10: data.liquid_year_3_10?.toString() || "",
        liquid_year_10_plus: data.liquid_year_10_plus?.toString() || "",
        ai_note: data.ai_note || null
      };
    }
    return {
      aum: "",
      target_return: "",
      target_volatility: "",
      max_drawdown: "",
      liquid_month_0_3: "",
      liquid_month_3_12: "",
      liquid_year_1_3: "",
      liquid_year_3_10: "",
      liquid_year_10_plus: "",
      ai_note: null,
      is_ai: false
    };
  }, [data]);

  const onSubmit = async (values: IPortfolioConstructionInvestmentGoal) => {
    try {
      const filteredValues = Object.entries(values).reduce((acc, [key, value]) => {
        if (key === "is_ai") return { ...acc, [key]: value };
        if (value && value !== "0" && value !== "") {
          return { ...acc, [key]: value };
        }
        return acc;
      }, {} as IPortfolioConstructionInvestmentGoal);

      if (Object.keys(filteredValues).length <= 1) {
        return;
      }

      await createGoals(investorId, filteredValues);
    } catch (error) {
      console.log(error);
    }
  };

  const { values, setFieldValue, handleSubmit, errors, touched, resetForm, setFieldError } = useFormik<FormValues>({
    initialValues: getFormValues(),
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values: FormValues) => {
      const numericValues: IPortfolioConstructionInvestmentGoal = {
        aum: Number(values.aum),
        target_return: Number(values.target_return),
        target_volatility: Number(values.target_volatility),
        max_drawdown: Number(values.max_drawdown),
        liquid_month_0_3: Number(values.liquid_month_0_3),
        liquid_month_3_12: Number(values.liquid_month_3_12),
        liquid_year_1_3: Number(values.liquid_year_1_3),
        liquid_year_3_10: Number(values.liquid_year_3_10),
        liquid_year_10_plus: Number(values.liquid_year_10_plus),
        is_ai: values.is_ai,
        ai_note: values.ai_note
      };
      onSubmit(numericValues);
    }
  });

  const handleCancel = () => {
    resetForm({ values: getFormValues() });
    setIsOpen(false);
  };

  const handleLiquidityClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const hasInitialValues = data && Object.values(data).some((value) => value !== "");

  const calculateLiquidityTotal = (values: FormValues) => {
    return [
      values.liquid_month_0_3,
      values.liquid_month_3_12,
      values.liquid_year_1_3,
      values.liquid_year_3_10,
      values.liquid_year_10_plus
    ].reduce((sum, value) => {
      const num = Number(value);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  };

  const formatNumber = (value: string | number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(value));
  };

  const liquidErrors = Boolean(
    errors.liquid_month_0_3 ||
    errors.liquid_month_3_12 ||
    errors.liquid_year_1_3 ||
    errors.liquid_year_3_10 ||
    errors.liquid_year_10_plus
    // errors.liquid_sum
  );

  const isDisabled = useMemo(() => {
    const hasAnyValue = Object.entries(values).some(([key, value]) => {
      if (key === "is_ai" || key === "ai_note") return false;
      return value !== "" && value !== null && value !== undefined;
    });
    return !hasAnyValue;
  }, [values]);

  return (
    <form onSubmit={handleSubmit}>
      <Stack
        direction="column"
        sx={{
          backgroundColor: "white",
          p: "16px",
          ...(liquidErrors &&
            isOpen && {
              paddingBottom: "60px"
            }),
          borderRadius: "8px",
          boxShadow: "0 1px 2px 0 #1212171a"
        }}
      >
        <AsyncContentMui isLoading={loading}>
          <Stack direction="column" justifyContent="center" alignItems="flex-start" gap="10px">
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
              <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#071F35" }}>Investment Goals</Typography>
              {isOpen ? (
                <Button variant="text" onClick={handleCancel}>
                  Cancel
                </Button>
              ) : (
                !isViewerPermission &&
                !hasInitialValues &&
                !loading && (
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: "#071F35", color: "white" }}
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    Add Goals
                  </Button>
                )
              )}
            </Stack>
            {!isOpen && !!hasInitialValues && (
              <Stack flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
                <Stack direction="row" gap="10px" alignItems="center">
                  {values.aum && (
                    <Chip
                      sx={{ height: "24px" }}
                      label={
                        <Stack direction="row" gap="10px" alignItems="center">
                          <Typography fontSize="12px" sx={{ color: "#496682" }}>
                            AUM
                          </Typography>
                          <Typography fontSize="12px" sx={{ color: "#071F35" }}>
                            {formatNumber(values.aum)}
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                  {values.target_return && (
                    <Chip
                      sx={{ height: "24px" }}
                      label={
                        <Stack direction="row" gap="10px" alignItems="center">
                          <Typography fontSize="12px" sx={{ color: "#496682" }}>
                            Target Return
                          </Typography>
                          <Typography fontSize="12px" sx={{ color: "#071F35" }}>
                            {values.target_return}%
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                  {values.target_volatility && (
                    <Chip
                      sx={{ height: "24px" }}
                      label={
                        <Stack direction="row" gap="10px" alignItems="center">
                          <Typography fontSize="12px" sx={{ color: "#496682" }}>
                            Target Volatility
                          </Typography>
                          <Typography fontSize="12px" sx={{ color: "#071F35" }}>
                            {values.target_volatility}%
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                  {values.max_drawdown && (
                    <Chip
                      sx={{ height: "24px" }}
                      label={
                        <Stack direction="row" gap="10px" alignItems="center">
                          <Typography fontSize="12px" sx={{ color: "#496682" }}>
                            Max Drawdown
                          </Typography>
                          <Typography fontSize="12px" sx={{ color: "#071F35" }}>
                            {formatNumber(values.max_drawdown)}
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                  {(values.liquid_month_0_3 ||
                    values.liquid_month_3_12 ||
                    values.liquid_year_1_3 ||
                    values.liquid_year_3_10 ||
                    values.liquid_year_10_plus) && (
                    <Chip
                      label={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <span>Liquidity Needs</span>
                          <ArrowDropDownIcon sx={{ fontSize: 16 }} />
                        </Stack>
                      }
                      onClick={handleLiquidityClick}
                      sx={{ bgcolor: "#F5F7F7", color: "#496682", height: "24px" }}
                    />
                  )}
                  {!isViewerPermission && (
                    <IconButton
                      onClick={() => setIsOpen(true)}
                      sx={{ backgroundColor: "#F5F7F7", height: "24px", width: "24px" }}
                    >
                      <EditIconOutlined sx={{ width: "15px", height: "15px", color: "#071F35" }} />
                    </IconButton>
                  )}
                </Stack>
                {!isViewerPermission && (
                  <Stack direction="row" alignItems="center" gap="5px">
                    <Button
                      variant="contained"
                      startIcon={
                        values.is_ai ? (
                          <DoneIcon sx={{ fill: "white" }} />
                        ) : (
                          <PsychologyReverse sx={{ fill: "white" }} />
                        )
                      }
                      sx={{
                        bgcolor: "#C09560",
                        height: "24px",
                        minHeight: "24px",
                        "&:hover": { bgcolor: "#9A774C" }
                      }}
                      onClick={() => {
                        setFieldValue("is_ai", true);
                        handleSubmit();
                      }}
                      loading={createGoalsLoading}
                    >
                      {values.is_ai ? "AI Applied" : "AI Construction"}
                    </Button>
                    {values.ai_note && values.is_ai && (
                      <IconButton onClick={(e) => setPopupAnchor(e.currentTarget)}>
                        <InfoOutlined sx={{ fontSize: 18, cursor: "pointer", fill: "#C09560" }} />
                      </IconButton>
                    )}
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
          <Collapse in={isOpen}>
            <Stack gap="10px">
              <Stack direction="row" paddingTop="10px" gap="10px">
                <NumberInput
                  label="AUM"
                  value={values.aum}
                  onChange={(e) => setFieldValue("aum", e.target.value)}
                  error={touched.aum && Boolean(errors.aum)}
                  helperText={touched.aum && errors.aum}
                  numberInputProps={{
                    // prefix: "$",
                    allowNegative: false,
                    isNumericString: true,
                    decimalScale: 2
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        sx={{
                          color: "#071F35",
                          fontSize: "14px",
                          marginRight: "8px"
                        }}
                      >
                        $
                      </Typography>
                    )
                  }}
                  sx={{ flex: 1 }}
                />

                <NumberInput
                  label="Target Return"
                  value={values.target_return}
                  onChange={(e) => setFieldValue("target_return", e.target.value)}
                  error={touched.target_return && Boolean(errors.target_return)}
                  helperText={touched.target_return && errors.target_return}
                  numberInputProps={{
                    allowNegative: false,
                    isNumericString: true,
                    decimalScale: 2,
                    thousandSeparator: false
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography
                        sx={{
                          color: "#071F35",
                          fontSize: "14px",
                          marginRight: "8px"
                        }}
                      >
                        %
                      </Typography>
                    )
                  }}
                  sx={{ flex: 1 }}
                />
                <NumberInput
                  label="Target Volatility"
                  value={values.target_volatility}
                  onChange={(e) => setFieldValue("target_volatility", e.target.value)}
                  error={touched.target_volatility && Boolean(errors.target_volatility)}
                  helperText={touched.target_volatility && errors.target_volatility}
                  numberInputProps={{
                    allowNegative: false,
                    isNumericString: true,
                    decimalScale: 2,
                    thousandSeparator: false
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography
                        sx={{
                          color: "#071F35",
                          fontSize: "14px",
                          marginRight: "8px"
                        }}
                      >
                        %
                      </Typography>
                    )
                  }}
                  sx={{ flex: 1 }}
                />
                <NumberInput
                  label="Max Drawdown"
                  value={values.max_drawdown}
                  onChange={(e) => setFieldValue("max_drawdown", e.target.value)}
                  error={touched.max_drawdown && Boolean(errors.max_drawdown)}
                  helperText={touched.max_drawdown && errors.max_drawdown}
                  numberInputProps={{
                    // prefix: "$",
                    allowNegative: false,
                    isNumericString: true,
                    decimalScale: 2
                  }}
                  InputProps={{
                    startAdornment: (
                      <Typography
                        sx={{
                          color: "#071F35",
                          fontSize: "14px",
                          marginRight: "8px"
                        }}
                      >
                        $
                      </Typography>
                    )
                  }}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, color: "#071F35" }}>
                  Liquidity Needs
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" gap="10px" alignItems="center" justifyContent="flex-start">
                    <NumberInput
                      label="0-3 Month"
                      value={values.liquid_month_0_3}
                      onChange={(e) => setFieldValue("liquid_month_0_3", e.target.value)}
                      error={touched.liquid_month_0_3 && Boolean(errors.liquid_month_0_3)}
                      helperText={touched.liquid_month_0_3 && errors.liquid_month_0_3}
                      numberInputProps={{
                        allowNegative: false,
                        isNumericString: true,
                        thousandSeparator: false
                      }}
                      InputProps={{
                        endAdornment: (
                          <Typography
                            sx={{
                              color: "#071F35",
                              fontSize: "14px",
                              marginRight: "8px"
                            }}
                          >
                            %
                          </Typography>
                        )
                      }}
                      sx={{
                        flex: 1,
                        maxWidth: "100px",
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          top: "35px",
                          minHeight: "20px",
                          margin: "4px 2px"
                        }
                      }}
                    />
                    <NumberInput
                      label="3-12 Month"
                      value={values.liquid_month_3_12}
                      onChange={(e) => setFieldValue("liquid_month_3_12", e.target.value)}
                      error={touched.liquid_month_3_12 && Boolean(errors.liquid_month_3_12)}
                      helperText={touched.liquid_month_3_12 && errors.liquid_month_3_12}
                      numberInputProps={{
                        allowNegative: false,
                        isNumericString: true,
                        thousandSeparator: false
                      }}
                      InputProps={{
                        endAdornment: (
                          <Typography
                            sx={{
                              color: "#071F35",
                              fontSize: "14px",
                              marginRight: "8px"
                            }}
                          >
                            %
                          </Typography>
                        )
                      }}
                      sx={{
                        flex: 1,
                        maxWidth: "100px",
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          top: "35px",
                          minHeight: "20px",
                          margin: "4px 2px"
                        }
                      }}
                    />
                    <NumberInput
                      label="1-3 Years"
                      value={values.liquid_year_1_3}
                      onChange={(e) => setFieldValue("liquid_year_1_3", e.target.value)}
                      error={touched.liquid_year_1_3 && Boolean(errors.liquid_year_1_3)}
                      helperText={touched.liquid_year_1_3 && errors.liquid_year_1_3}
                      numberInputProps={{
                        allowNegative: false,
                        isNumericString: true,
                        thousandSeparator: false
                      }}
                      InputProps={{
                        endAdornment: (
                          <Typography
                            sx={{
                              color: "#071F35",
                              fontSize: "14px",
                              marginRight: "8px"
                            }}
                          >
                            %
                          </Typography>
                        )
                      }}
                      sx={{
                        flex: 1,
                        maxWidth: "100px",
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          top: "35px",
                          minHeight: "20px",
                          margin: "4px 2px"
                        }
                      }}
                    />
                    <NumberInput
                      label="3-10 Years"
                      value={values.liquid_year_3_10}
                      onChange={(e) => setFieldValue("liquid_year_3_10", e.target.value)}
                      error={touched.liquid_year_3_10 && Boolean(errors.liquid_year_3_10)}
                      helperText={touched.liquid_year_3_10 && errors.liquid_year_3_10}
                      numberInputProps={{
                        allowNegative: false,
                        isNumericString: true,
                        thousandSeparator: false
                      }}
                      InputProps={{
                        endAdornment: (
                          <Typography
                            sx={{
                              color: "#071F35",
                              fontSize: "14px",
                              marginRight: "8px"
                            }}
                          >
                            %
                          </Typography>
                        )
                      }}
                      sx={{
                        flex: 1,
                        maxWidth: "100px",
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          top: "35px",
                          minHeight: "20px",
                          margin: "4px 2px"
                        }
                      }}
                    />
                    <NumberInput
                      label="10+ Years"
                      value={values.liquid_year_10_plus}
                      onChange={(e) => setFieldValue("liquid_year_10_plus", e.target.value)}
                      error={touched.liquid_year_10_plus && Boolean(errors.liquid_year_10_plus)}
                      helperText={touched.liquid_year_10_plus && errors.liquid_year_10_plus}
                      numberInputProps={{
                        allowNegative: false,
                        isNumericString: true,
                        thousandSeparator: false
                      }}
                      InputProps={{
                        endAdornment: (
                          <Typography
                            sx={{
                              color: "#071F35",
                              fontSize: "14px",
                              marginRight: "8px"
                            }}
                          >
                            %
                          </Typography>
                        )
                      }}
                      sx={{
                        flex: 1,
                        maxWidth: "100px",
                        "& .MuiFormHelperText-root": {
                          position: "absolute",
                          top: "35px",
                          minHeight: "20px",
                          margin: "4px 2px"
                        }
                      }}
                    />
                    <Stack
                      direction="column"
                      gap="10px"
                      alignItems="center"
                      justifyContent="flex-start"
                      // position="relative"
                    >
                      <Typography
                        sx={{
                          color: "#071F35",
                          // color: errors.liquid_sum ? "#D93E3E !important" : "#071F35",
                          fontWeight: "500"
                        }}
                      >
                        Total: {calculateLiquidityTotal(values)}%
                      </Typography>
                      {/* {errors.liquid_sum && (
                        <Typography
                          sx={{
                            color: "#D93E3E !important",
                            fontSize: "14px",
                            position: "absolute",
                            top: "20px",
                            left: "0",
                            width: "200px"
                          }}
                        >
                          {errors.liquid_sum}
                        </Typography>
                      )} */}
                    </Stack>
                  </Stack>
                  <Stack direction="row" gap="10px" alignItems="flex-start">
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{ bgcolor: "#071F35" }}
                      onClick={() => setFieldValue("is_ai", false)}
                      disabled={isDisabled}
                    >
                      Apply
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PsychologyReverse sx={{ fill: "white" }} />}
                      onClick={() => {
                        setFieldValue("is_ai", true);
                        handleSubmit();
                      }}
                      disabled={!values.target_return}
                      loading={createGoalsLoading}
                      sx={{
                        bgcolor: "#C09560",
                        "&:hover": { bgcolor: "#9A774C" }
                      }}
                    >
                      AI Allocation
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Collapse>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left"
            }}
          >
            <Box sx={{ p: "20px", minWidth: 200 }}>
              <Typography variant="h6" sx={{ mb: 1, color: "#071F35" }}>
                Liquidity Needs
              </Typography>
              {values.liquid_month_0_3 && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography>0-3 month:</Typography>
                  <Typography>{values.liquid_month_0_3}%</Typography>
                </Stack>
              )}
              {values.liquid_month_3_12 && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography>3-12 month:</Typography>
                  <Typography>{values.liquid_month_3_12}%</Typography>
                </Stack>
              )}
              {values.liquid_year_1_3 && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography>1-3 years:</Typography>
                  <Typography>{values.liquid_year_1_3}%</Typography>
                </Stack>
              )}
              {values.liquid_year_3_10 && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography>3-10 years:</Typography>
                  <Typography>{values.liquid_year_3_10}%</Typography>
                </Stack>
              )}
              {values.liquid_year_10_plus && (
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography>10+ years:</Typography>
                  <Typography>{values.liquid_year_10_plus}%</Typography>
                </Stack>
              )}
            </Box>
          </Popover>
          {values.ai_note && (
            <NoteHintPopover
              note={values.ai_note}
              onRemoveAIConstruction={onRemoveAIConstruction}
              onClose={() => setPopupAnchor(null)}
              anchorEl={popupAnchor}
            />
          )}
        </AsyncContentMui>
      </Stack>
    </form>
  );
};
